import {
    IDocVerifierManager, IPasswordParam, IReportFileType, IReportPasswordType, IReportOperateRes,
} from '@src/slide/domain/slide-app/core/interfaces/doc-verifier-manager.interface';
import { ICommandData, MutationType } from '@tencent/slide-sdk-model';
import { ILocalEditEvents, LocalEditEvents } from '@src/slide/domain/slide-app/scenes/local-edit/core/interfaces/local-edit-events.interface';
import { ILocalEditContext } from '@src/slide/domain/slide-app/scenes/local-edit/core/interfaces/local-edit-context.interface';
import { PasswordError } from '@src/slide/domain/slide-app/scenes/local-edit/core/interfaces/local-edit-data-converter.interface';
import { ILogger } from '@src/slide/domain/slide-app/core/interfaces/logger.interface';
import appConfigInstance, { setDefaultConfig } from '@src/slide/infrastructure/module/dependent/config/tob/app-config';
import { CustomConfig } from '@src/slide/infrastructure/module/dependent/config/config-manager-constants';
import { createTimestampGenerator } from '@src/slide/domain/slide-app/utils/trace-format';
import { IMqq, SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import type { PasswordModalProps, ModalReturnType, PasswordParam } from '@tencent/docs-crypto-kit';
import { DocxInfo } from '@tencent/mega-sdk-protocols';
import i18n from '@i18next/i18n';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { PPTEditorStateEnumType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { ISlideBaseAppServices } from '@src/slide/application/entry/toc/base/slide-base-app-services.interface';
import { PadType } from '@tencent/docs-mime-types';

const Z_INDEX = 10004;
const MODULE = '[DocVerifierManager]';
const USE_NATIVE_PASSWORD = setDefaultConfig(appConfigInstance.getConfig(CustomConfig.useNativePassword as any), false);


export abstract class BaseDocVerifierManager implements IDocVerifierManager {
    private isReadOnly = false;
    private cryptoKitModel;
    private openVerifierPopup: ModalReturnType;
    private passwordParam: IPasswordParam;
    private openPassword: string;

    public constructor(
        private logger: ILogger,
        private context: ILocalEditContext,
        private emitter: ILocalEditEvents,
        protected mqq: IMqq,
    ) {
        // 预拉取密码弹框组件
        this.getCryptoKit();
        // 监听文档加载成功，关闭打开加密弹框
        this.listenOpenVerifierPass();
    }

    public getOpenPassword(): string | undefined {
        return this.openPassword;
    }

    public getIsReadOnly(): boolean {
        return this.isReadOnly;
    }

    /**
     * 检查是否存在打开密码，是则校验，否则跳过
     * @param error
     * @param code
     * @param continueWithPassword
     * @param publishDocxInfo
     */
    public async checkOpenVerifier(
        error,
        code,
        continueWithPassword,
        publishDocxInfo: (info: DocxInfo) => void,
    ): Promise<void> {
        const title = this.context.getTitle();
        const timestamp = createTimestampGenerator();

        this.logger.info(MODULE, '[load:checkOpenVerifier]', title, error, timestamp());

        // 通知桌面端顶部栏显示加密标识
        publishDocxInfo({ hasEditPassword: true });

        // 手Q使用原生密码弹框
        if (USE_NATIVE_PASSWORD) {
            this.logger.info(MODULE, 'NativePasswordModal', 'show()');
            this.mqq.invoke('docx', 'notifyLocalFilePasswordError', { code }, (result) => {
                this.logger.info(MODULE, 'NativePasswordModal', 'continueWithPassword()');
                continueWithPassword(result?.password);
            });
            this.emitter.emit(LocalEditEvents.LoadDocumentUseNativePassword);
            return;
        }

        // 桌面端与客户端使用通用弹框
        if (error === PasswordError.Required) {
            this.logger.info(MODULE, 'PasswordModal', 'show()');
            let config = {
                zIndex: Z_INDEX,
                report: {
                    padType: PadType.Slide,
                },
                onOk: (value) => {
                    if (value) {
                        this.openPassword = value;
                        this.logger.info(MODULE, 'PasswordModal', 'continueWithPassword()');
                        continueWithPassword(value);
                        this.openVerifierPopup?.setOkBtnLoading?.(true);
                    } else {
                        this.openVerifierPopup?.setErrorMessage?.(i18n.t('密码不能为空'));
                    }
                },
            };
            config = Object.assign(config, this.getOpenVerifierConfig());
            this.openVerifierPopup = await this.showPasswordModal(config);
            this.emitter.emit(LocalEditEvents.LoadDocumentPasswordRequired);
        } else {
            this.logger.info(MODULE, 'PasswordModal', 'showPasswordError()');
            this.openVerifierPopup?.setErrorMessage?.(i18n.t('密码出错，请重新输入'));
            this.openVerifierPopup?.setOkBtnLoading?.(false);
            this.emitter.emit(LocalEditEvents.LoadDocumentPasswordInvalid);
            this.reportTdw(TDWActionType.PASSWORD_CLICK, IReportOperateRes.fail, IReportPasswordType.open);
        }
    }

    /**
     * 检查是否存在编辑密码，是则校验，否则跳过
     * @param headData
     * @param publishDocxInfo
     */
    public checkModifyVerifier(
        headData: ICommandData,
        publishDocxInfo: (info: DocxInfo) => void,
    ): Promise<void> {
        const modifyVerifier = headData.mutations?.find(e => e.type === MutationType.SetPresentationProperties)
            ?.setPresentationProperties?.modifyVerifier || {};
        const { cryptAlgorithmSid, spinCount, saltData, hashData } = modifyVerifier;
        if (
            !this.isNumValid(hashData)
            || !this.isNumValid(saltData)
            || !this.isNumValid(spinCount)
            || !this.isNumValid(cryptAlgorithmSid)
        ) {
            // 不存在编辑密码，继续加载
            return Promise.resolve();
        }

        const title = this.context.getTitle();
        const timestamp = createTimestampGenerator();
        this.logger.info(MODULE, '[load:checkModifyVerifier]', title, timestamp());

        this.passwordParam = {
            cryptAlgorithmSid: cryptAlgorithmSid!.toString(),
            spinCount: spinCount!.toString(),
            saltData: saltData!.toString(),
            hashData: hashData!.toString(),
        };
        // 通知桌面端顶部栏显示加密标识
        publishDocxInfo({ hasEditPassword: true });

        // 如果之前已有打开的打开密码弹框，强制关闭，防止双层蒙版
        this.openVerifierPopup?.close();

        // 默认文档只读，只是title不标识，避免不可控因素去掉弹层遮罩，导致文档可编
        this.switchDocReadOnly(true);

        // 存在编辑密码，等待验证通过才继续加载
        return new Promise((resolve) => {
            // 弹编辑密码验证弹框
            this.showModifyVerifier().then(() => {
                resolve();
            });
        });
    }

    /**
     * 只读模式下点击编辑再次弹出编辑密码弹框
     */
    public showModifyVerifier(): Promise<ModalReturnType> {
        let config = {
            zIndex: Z_INDEX,
            report: {
                padType: PadType.Slide,
            },
        };
        config = Object.assign(config, this.getModifyVerifierConfig());
        // 弹编辑密码验证弹框
        return this.validateDocumentPassword({ ...this.passwordParam, ...config });
    }

    /**
     * dynamic import
     * https://doc.weixin.qq.com/doc/w3_AZ4AIQZKAOw4Sbgw9pDTjaNMP24tI?scode=AJEAIQdfAAojEdn7uZAZ4AIQZKAOw
     * @protected
     */
    protected getCryptoKit(): Promise<any> {
        if (!this.cryptoKitModel) {
            this.cryptoKitModel = import('@tencent/docs-crypto-kit');
        }
        return this.cryptoKitModel;
    }

    protected reportTdw(action: string, ver5?: string, ver6?: string) {
        tdw({
            opername: TDWOperationType.DETAIL_PAGE,
            module: TDWModuleType.PASSWORD_POPUP,
            action,
            ver5: ver5 ?? '',
            ver6: ver6 ?? '',
            ver7: IReportFileType.local,
        });
    }

    /**
     * 切换当前文档设置只读模式
     * @private
     */
    protected switchDocReadOnly(readOnly: boolean): void {
        SlideDataServices.PrivilegeManager.promise().then((privilegeManager) => {
            privilegeManager.changeEditable(!readOnly);
        });
        AppServiceProvider.getInstance().waitForReady((appService: ISlideBaseAppServices) => {
            appService.viewableServiceLayer.userPriviledgeService.setPPTEditorState({
                state: readOnly ? PPTEditorStateEnumType.COPYABLE : PPTEditorStateEnumType.EDITABLE,
            });
        });
        this.isReadOnly = readOnly;
    }

    /**
     * 监听文档加载成功，关闭打开加密弹框
     * @private
     */
    private listenOpenVerifierPass() {
        this.emitter.on(LocalEditEvents.LoadDocumentAllChunksSuccess, () => {
            if (this.openVerifierPopup) {
                this.openVerifierPopup.close();
                this.reportTdw(TDWActionType.PASSWORD_CLICK, IReportOperateRes.success, IReportPasswordType.open);
            }
        });
    }

    private isNumValid(num: number | null | undefined | string): boolean {
        return !!(num || (num === 0));
    }

    protected abstract getOpenVerifierConfig(): PasswordModalProps;

    protected abstract getModifyVerifierConfig(): PasswordParam;

    /**
     * 打开密码弹框
     * @param props
     * @protected
     */
    protected abstract showPasswordModal(props: PasswordModalProps): Promise<ModalReturnType>;

    /**
     * 编辑密码弹框
     * @param param
     * @protected
     */
    protected abstract validateDocumentPassword(param: PasswordParam): Promise<ModalReturnType>;
}
